define('viewModels/privileged/qm/RecordingView',[
    'lib/knockout',
    'lib/lodash4',
    'jquery',
    'util/ininAudioPlayer',
    'util/ininAudioUtils',
    'services/authService',
    'services/loggingService',
    'services/notificationService',
    'services/qualityManagementService',
    'services/userService',
    'services/qm/qualityAuth',
    'util/errorCallbackFactory',
    'viewModels/widgets/audio/AudioPlayerView',
    'viewModels/privileged/qm/ChatDetailsView',
    'viewModels/privileged/qm/EmailDetailsView',
    'viewModels/privileged/qm/ScreenRecordingDetailsView',
    'viewModels/privileged/qm/ConversationDetailsView',
    'viewModels/privileged/qm/CalibrationResultsView',
    'viewModels/privileged/qm/EvaluationSummaryView',
    'viewModels/privileged/qm/EvaluationView',
    'viewModels/privileged/qm/CreateEvaluationView',
    'viewModels/privileged/qm/RecordingRestoreDetailsView',
    'viewModels/privileged/qm/util/FileStateEnum',
    'viewModels/widgets/TabbedContentView',
    'viewModels/ViewModel'

], function (ko,
             _,
             $,
             IninAudioPlayer,
             ininAudioUtils,
             authService,
             LOG,
             notificationService,
             qualityManagementService,
             userService,
             qualityAuthorization,
             errorCallbackFactory,
             AudioPlayerView,
             ChatDetailsView,
             EmailDetailsView,
             ScreenRecordingDetailsView,
             ConversationDetailsView,
             CalibrationResultsView,
             EvaluationSummaryView,
             EvaluationViewWrapper,
             CreateEvaluationView,
             RecordingRestoreDetailsView,
             FileStateEnum,
             TabbedContentView,
             ViewModel
) {
    'use strict';

    /**
     * The RecordingView ViewModel.
     *
     * note: be aware that if you add a property to the viewModel instance,
     * and use it in the recordingManager.html template, you may need to replicate that property
     * across any other viewModels that get added to the RecordingManagerView.recordingViews array.
     * ex: MessagesRecordingView
     */
    var RecordingView = ViewModel.extend(function(conversationId, recordingModel, options, conversationObservable){
        var self = this;

        var audioOptions = _.assign({ autoplay: false }, _.omit(options, 'displayed'));

        self.displayed = ko.observable(options ? !!options.displayed : false);
        self.conversation = ko.isObservable(conversationObservable) ? conversationObservable : ko.observable(conversationObservable);
        self.conversationId = ko.observable(conversationId);
        self.recording = ko.observable();
        self.chatDetailsView = ko.observable();
        self.emailDetailsView = ko.observable();
        self.screenRecordingDetailsView = ko.observable();
        self.recordingRestoreDetails = self.subView(RecordingRestoreDetailsView, self.recording);
        self.hasAudio = ko.observable(false);
        self.hasChat = ko.observable(false);
        self.hasEmail = ko.observable(false);
        self.hasMessages = false; // Does not need to be observable. Will always be false
        self.hasScreenRecording = ko.observable(false);
        self.audioPlayerView = self.subView(AudioPlayerView, null, self.conversationId(), audioOptions);
        self.isRecordingLoading = ko.observable(true);
        self.isRecordingAvailable = ko.observable(false);
        self.showLongRunningMessage = ko.observable(false);
        self.recordingRequestError = ko.observable(false);
        self.keywordSpots = ko.observableArray();
        self.isDeleted = recordingModel.fileState === FileStateEnum.DELETED;
        self.recordingDeletedTitle = 'playback.recordingIsDeleted.title';
        self.recordingDeletedDescription = 'playback.recordingIsDeleted.description';
        self.isAvailableAudioRecordingWithMissingMedia = self.computed(function() {
            var recording = self.recording();
            if (recording) {
                var media = ko.unwrap(recordingModel.media);
                return media === "audio" &&
                    self.isRecordingAvailable() &&
                    !self.hasAudio();
            }
            return false;
        });

        _.assign(self, audioOptions);

        //Adjust annotations for display
        self.adjustAnnotations = function(annotations) {
            _.each(annotations, function (annotation) {

                //Adjust secure pauses to zero length and
                //move actual length to description
                if(annotation.type==='Pause') {
                    annotation.description += ' ' +
                        ko.i18n.renderString({
                            'name': 'annotation.secondsDescription',
                            'data': {seconds: (annotation.absoluteDurationMs / 1000.0).toFixed(1)}
                        });
                    annotation.durationMs = 0;
                }

            });
        };

        var loadRecordingResponse = function(recordingModel){

            if (recordingModel.annotations) {
                self.adjustAnnotations(recordingModel.annotations);
            }

            self.showLongRunningMessage(false);
            self.isRecordingLoading(false);
            self.recordingRequestError(false);
            self.recording(recordingModel);
        };

        var setRecording = function(recordingModel) {

            self.conversationId = recordingModel.conversationId;
            loadRecordingResponse(recordingModel);

        };

        setRecording(recordingModel);

        var isAudioElementRendered = ko.observable(false);
        self.audioRendered = function () {
            isAudioElementRendered(true);
        };

        self.hasAnnotationText = self.computed(function() {
            return self.audioPlayerView.annotationTextInput().length > 0;
        });

        self.computed(function () {
            if (self.recording()) {
                var fileState = ko.unwrap(self.recording().fileState);
                if (fileState) {
                    self.isRecordingAvailable(fileState.toLowerCase() === FileStateEnum.AVAILABLE.toLowerCase() ||
                                              fileState.toLowerCase() === FileStateEnum.RESTORED.toLowerCase());
                }

                self.hasAudio(ko.unwrap(self.recording().media) === "audio" && (self.recording().mediaUri || self.recording().mediaUris));
                if (isAudioElementRendered()) {
                    if (self.recording().mediaUris) {
                        var channels = [];
                        _.each(self.recording().mediaUris, function (mediaInfo) {
                            var unwrapped = ko.toJS(mediaInfo);
                            channels.push({
                                audioUri: unwrapped.mediaUri,
                                waveformUri: unwrapped.waveUri,
                                waveformData: unwrapped.waveformData
                            });
                        });

                        if (!_.isEmpty(self.audioPlayerView.audioChannels())) {
                            self.disposeSubView(self.audioPlayerView);
                            self.audioPlayerView = self.subView(AudioPlayerView, null, ko.unwrap(self.conversationId), audioOptions);
                        }
                        self.audioPlayerView.setChannels(channels);
                    }
                    else if (self.recording().mediaUri) {
                        var unwrapped = ko.toJS(self.recording);
                        self.audioPlayerView.setChannels([{
                            audioUri: unwrapped.mediaUri,
                            waveformUri: unwrapped.waveUri
                        }]);
                    }
                    if (self.recording().annotations) {
                        self.audioPlayerView.annotationsList(self.recording().annotations);

                        var keywordAnnotations = _.filter(ko.unwrap(self.recording().annotations), function (annotation) {
                            return ko.unwrap(annotation.type) === "Keyword Spot";
                        });

                        self.keywordSpots(_.map(keywordAnnotations, function (annotation) {
                            annotation.playAtLocation = function () {
                                self.audioPlayerView.setPositionMs(annotation.location);
                                self.audioPlayerView.play();
                            };
                            return annotation;
                        }));
                    }
                }

                self.hasEmail(self.recording().emailTranscript || self.recording().media === "email");
                if (self.hasEmail() && !self.emailDetailsView()) {
                    self.emailDetailsView(self.subView(EmailDetailsView, self.recording, self.recordingRestoreDetails, options, self.conversation));
                    self.displayed = self.computed({
                        read: function () {
                            return self.emailDetailsView().emailMessages()[0].showEmail();
                        },
                        write: function (value) {
                            self.emailDetailsView().emailMessages()[0].showEmail(value);
                        }
                    });
                }

                self.hasChat(self.recording().transcript || self.recording().media === "chat");
                if (self.hasChat() && self.isRecordingAvailable()) {
                    self.chatDetailsView(self.subView(ChatDetailsView, self.recording().transcript));
                }

                self.hasScreenRecording(self.recording().media === "screen" && (self.recording().mediaUri || self.recording().mediaUris));
                if (!self.hasEmail() && self.hasScreenRecording()) {
                    self.screenRecordingDetailsView(self.subView(ScreenRecordingDetailsView, window.parent.location.href));
                }
            }
        });

        self.pause = function() {
            if (self.audioPlayerView.isPlaying()){
                self.audioPlayerView.pause();
            }
        };

        self.play = function() {
            if (self.audioPlayerView.isPlaying()){
                self.audioPlayerView.play();
            }
        };

        self.saveAnnotation = function () {
            var annotationText = self.audioPlayerView.annotationTextInput();
            if(annotationText.length > 0) {
                /*
                 * ininAudioPlayer does not rewind (currently) once the playback finishes so if you
                 * create an annotation after the initial playback and use getPosition() of the player,
                 * your annotation will appear at the end, instead of the start
                 */
                var position = (self.audioPlayerView.percentPosition() * self.audioPlayerView.getDuration()) / 100;
                qualityManagementService.createAnnotation(self.recording(), annotationText, position)
                    .then(function(annotation){
                        self.audioPlayerView.annotationsList.push(annotation);
                        self.audioPlayerView.annotationTextInput('');
                    })
                    .catch(errorCallbackFactory.createCallback({ namespace: "playback.recordingAnnotation"}));
            }
        };
    });

    return RecordingView;
});

